import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core'
import { MAX_VOICE_BYTES, MAX_VOICE_DURATION_MS } from '../../shared/voiceCard'

interface NativeVoice {
  requestPermission(): Promise<{ granted: boolean }>
  startRecording(options: { recordingId: string }): Promise<void>
  stopRecording(options: { recordingId: string }): Promise<{ base64: string; mime: string; durationMs: number }>
  cancelRecording(options?: { recordingId?: string }): Promise<void>
  playVoice(options: { base64: string; playbackId: string }): Promise<{ durationMs: number }>
  stopPlayback(options?: { playbackId?: string }): Promise<void>
  getPlaybackState(): Promise<{ playbackId: string; positionMs: number; playing: boolean }>
  addListener(event: string, listener: (event: { message?: string; playbackId?: string; recordingId?: string }) => void): Promise<PluginListenerHandle>
}
const NativeRecorder = registerPlugin<NativeVoice>('ShixuVoice')

export interface VoiceRecording { file: File; durationMs: number; clientId: string }
export interface VoiceCapture { stop(): Promise<VoiceRecording>; cancel(): Promise<void> }
let activeCapture: VoiceCapture | null = null
let cancelPlayback: (() => Promise<void>) | null = null
let captureGeneration = 0
let playbackGeneration = 0

export function nativeVoiceAvailable() {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('ShixuVoice')
}

export function voiceErrorMessage(error: unknown) {
  if (error instanceof Error) {
    if (['NotAllowedError', 'PermissionDeniedError', 'SecurityError'].includes(error.name)) return '需要麦克风权限才能发送语音，请在系统设置中允许诗序使用麦克风'
    if (['NotFoundError', 'DevicesNotFoundError'].includes(error.name)) return '没有找到可用麦克风'
    if (error.name === 'NotReadableError') return '麦克风正被其他程序占用，请结束通话后再试'
    if (error.message) return error.message
  }
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') return error.message
  return '录音失败，请检查麦克风后重试'
}

function stopTracks(stream: MediaStream) { stream.getTracks().forEach((track) => track.stop()) }
function recording(file: File, durationMs: number): VoiceRecording {
  if (!file.size || file.size > MAX_VOICE_BYTES) throw new Error('语音文件过大或为空，请重新录制')
  return { file, durationMs: Math.min(MAX_VOICE_DURATION_MS, durationMs), clientId: crypto.randomUUID() }
}
function supportedBrowserMime() {
  if (typeof MediaRecorder === 'undefined') throw new Error('当前浏览器不支持录音，请使用诗序 App 或更新浏览器')
  const mime = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm'].find((value) => MediaRecorder.isTypeSupported(value))
  if (!mime) throw new Error('当前浏览器没有可用的语音录音格式，请使用诗序 App')
  return mime
}

/** A tap on the voice-mode switch prepares permission, without retaining a live microphone. */
export async function prepareVoiceMicrophone() {
  if (Capacitor.isNativePlatform()) {
    if (!nativeVoiceAvailable()) throw new Error('发送语音需要更新诗序安装包，请安装最新版本')
    const result = await NativeRecorder.requestPermission()
    if (!result.granted) throw new Error('需要麦克风权限才能发送语音，请在系统设置中允许诗序使用麦克风')
    return
  }
  supportedBrowserMime()
  if (!navigator.mediaDevices?.getUserMedia) throw new Error('当前页面无法使用麦克风，请通过 HTTPS 打开诗序')
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
  stopTracks(stream)
}

/** Cancels pending permissions/recordings and stops playback before a call or route change. */
export async function stopVoiceAudio(notify = true) {
  captureGeneration += 1
  playbackGeneration += 1
  if (notify && typeof window !== 'undefined') window.dispatchEvent(new Event('shixu:voice-stop'))
  const capture = activeCapture
  activeCapture = null
  const stop = cancelPlayback
  cancelPlayback = null
  await Promise.allSettled([capture?.cancel(), stop?.()])
}

export async function startVoiceRecording(onInterrupted: (message: string) => void, onLimit: () => void): Promise<VoiceCapture> {
  await stopVoiceAudio(false)
  const generation = captureGeneration
  if (nativeVoiceAvailable()) {
    const recordingId = crypto.randomUUID()
    let interrupted: PluginListenerHandle | null = null
    let limit: PluginListenerHandle | null = null
    let finished = false
    async function removeListeners() {
      const handles = [interrupted, limit]; interrupted = null; limit = null
      await Promise.allSettled(handles.map((handle) => handle?.remove()))
    }
    interrupted = await NativeRecorder.addListener('recordingInterrupted', (event) => {
      if (event.recordingId !== recordingId) return
      finished = true
      if (activeCapture === capture) activeCapture = null
      void removeListeners()
      onInterrupted(event.message ?? '录音被中断，请重新录制')
    })
    limit = await NativeRecorder.addListener('recordingLimitReached', (event) => { if (event.recordingId === recordingId) onLimit() })
    const capture: VoiceCapture = {
      async stop() {
        if (finished) throw new Error('录音已结束，请重新录制')
        finished = true
        if (activeCapture === capture) activeCapture = null
        try {
          const value = await NativeRecorder.stopRecording({ recordingId })
          const bytes = Uint8Array.from(atob(value.base64), (char) => char.charCodeAt(0))
          return recording(new File([bytes], `语音-${Date.now()}.m4a`, { type: 'audio/mp4' }), value.durationMs)
        } finally { await removeListeners() }
      },
      async cancel() {
        if (finished) return
        finished = true
        if (activeCapture === capture) activeCapture = null
        try { await NativeRecorder.cancelRecording({ recordingId }) } finally { await removeListeners() }
      },
    }
    try {
      if (generation !== captureGeneration) { await removeListeners(); throw new Error('录音已取消') }
      await NativeRecorder.startRecording({ recordingId })
      if (generation !== captureGeneration) { await capture.cancel(); throw new Error('录音已取消') }
      activeCapture = capture
      return capture
    } catch (error) {
      await removeListeners()
      await NativeRecorder.cancelRecording({ recordingId }).catch(() => {})
      throw error
    }
  }
  if (Capacitor.isNativePlatform()) throw new Error('发送语音需要更新诗序安装包，请安装最新版本')
  const mime = supportedBrowserMime()
  if (!navigator.mediaDevices?.getUserMedia) throw new Error('当前页面无法使用麦克风，请通过 HTTPS 打开诗序')
  const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 } })
  if (generation !== captureGeneration) { stopTracks(stream); throw new Error('录音已取消') }
  let recorder: MediaRecorder
  try { recorder = new MediaRecorder(stream, { mimeType: mime, audioBitsPerSecond: 48_000 }) }
  catch (error) { stopTracks(stream); throw error }
  const chunks: Blob[] = []
  const startedAt = Date.now()
  let finished = false
  let settle: ((error?: Error) => void) | null = null
  recorder.ondataavailable = (event) => { if (event.data.size) chunks.push(event.data) }
  recorder.onerror = () => {
    stopTracks(stream)
    if (settle) settle(new Error('录音失败，请重新录制'))
    else { finished = true; onInterrupted('录音被中断，请重新录制') }
  }
  recorder.onstop = () => { stopTracks(stream); settle?.() }
  const capture: VoiceCapture = {
    async stop() {
      if (finished) throw new Error('录音已结束，请重新录制')
      finished = true
      if (activeCapture === capture) activeCapture = null
      const durationMs = Date.now() - startedAt
      try {
        await new Promise<void>((resolve, reject) => {
          const timer = window.setTimeout(() => { stopTracks(stream); reject(new Error('保存录音超时，请重新录制')) }, 8_000)
          settle = (error) => { window.clearTimeout(timer); error ? reject(error) : resolve() }
          if (recorder.state === 'inactive') settle()
          else recorder.stop()
        })
        const fileMime = mime.startsWith('audio/mp4') ? 'audio/mp4' : 'audio/webm'
        return recording(new File(chunks, `语音-${Date.now()}.${fileMime === 'audio/mp4' ? 'm4a' : 'webm'}`, { type: fileMime }), durationMs)
      } finally { stopTracks(stream); chunks.length = 0; settle = null }
    },
    async cancel() {
      if (finished) return
      finished = true
      if (activeCapture === capture) activeCapture = null
      recorder.ondataavailable = null; recorder.onerror = null; recorder.onstop = null
      try { if (recorder.state !== 'inactive') recorder.stop() } finally { stopTracks(stream); chunks.length = 0 }
    },
  }
  try { recorder.start(250) } catch (error) { await capture.cancel(); throw error }
  activeCapture = capture
  return capture
}

function blobBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('语音文件无法读取'))
    reader.onload = () => {
      const value = typeof reader.result === 'string' ? reader.result.split(',')[1] : ''
      value ? resolve(value) : reject(new Error('语音文件无法读取'))
    }
    reader.readAsDataURL(blob)
  })
}

/** One clip at a time. Native playback also works with the iPhone silent switch on. */
export async function playVoiceUrl(url: string, onProgress: (milliseconds: number) => void, onEnded: () => void): Promise<() => Promise<void>> {
  const playGeneration = ++playbackGeneration
  const previous = cancelPlayback
  cancelPlayback = null
  await previous?.()
  const generation = captureGeneration
  if (nativeVoiceAvailable()) {
    const response = await fetch(url)
    if (!response.ok) throw new Error('语音加载失败，请重试')
    const blob = await response.blob()
    if (blob.size > MAX_VOICE_BYTES) throw new Error('语音文件过大，无法播放')
    const base64 = await blobBase64(blob)
    if (generation !== captureGeneration || playGeneration !== playbackGeneration) throw new Error('播放已取消')
    const playbackId = crypto.randomUUID()
    let done = false
    let timer: number | undefined
    const finish = () => {
      if (done) return
      done = true
      window.clearInterval(timer)
      void listener.remove()
      if (cancelPlayback === stop) cancelPlayback = null
      onEnded()
    }
    const listener = await NativeRecorder.addListener('playbackEnded', (event) => { if (event.playbackId === playbackId) finish() })
    const stop = async () => { finish(); await NativeRecorder.stopPlayback({ playbackId }).catch(() => {}) }
    cancelPlayback = stop
    try {
      await NativeRecorder.playVoice({ base64, playbackId })
      if (done || generation !== captureGeneration || playGeneration !== playbackGeneration) { await stop(); return stop }
      timer = window.setInterval(() => {
        void NativeRecorder.getPlaybackState().then((state) => {
          if (done) return
          if (state.playbackId !== playbackId || !state.playing) finish()
          else onProgress(state.positionMs)
        }).catch(() => finish())
      }, 250)
      return stop
    } catch (error) { await stop(); throw error }
  }
  if (generation !== captureGeneration || playGeneration !== playbackGeneration) throw new Error('播放已取消')
  const audio = new Audio(url)
  let done = false
  const finish = () => {
    if (done) return
    done = true
    audio.pause()
    audio.removeAttribute('src')
    audio.onended = null; audio.ontimeupdate = null; audio.onerror = null
    if (cancelPlayback === stop) cancelPlayback = null
    onEnded()
  }
  const stop = async () => { finish() }
  audio.ontimeupdate = () => onProgress(audio.currentTime * 1000)
  audio.onended = finish
  audio.onerror = finish
  cancelPlayback = stop
  try { await audio.play(); return stop } catch (error) { finish(); throw error }
}
